/**
 * @typedef {Object} AssignedMetric
 * @property {string} metricId
 * @property {string} metricCode
 * @property {string} metricTitle
 * @property {string} framework
 * @property {string} category
 * @property {boolean} isCompleted
 */

export const CATEGORY_ORDER = [
  "General",
  "Environmental",
  "Social",
  "Governance",
];

const CATEGORY_RANK = {
  General: 0,
  Environmental: 1,
  Social: 2,
  Governance: 3,
};

const UNRANKED = 99;

export const CATEGORY_STYLES = {
  General:
    "border-slate-300/70 bg-slate-100 text-slate-700 dark:border-slate-500/40 dark:bg-slate-500/15 dark:text-slate-200",
  Environmental:
    "border-emerald-300/70 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300",
  Social:
    "border-blue-300/70 bg-blue-50 text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/15 dark:text-blue-300",
  Governance:
    "border-amber-300/70 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-300",
};

export const METRIC_CODE_STYLES = {
  General:
    "border-slate-300/70 bg-slate-50 text-slate-700 dark:border-slate-500/40 dark:bg-slate-500/10 dark:text-slate-200",
  Environmental:
    "border-emerald-300/70 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300",
  Social:
    "border-blue-300/70 bg-blue-50 text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300",
  Governance:
    "border-amber-300/70 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300",
};

const compareText = (left, right) =>
  String(left || "").localeCompare(String(right || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });

const rankCategory = (category) => CATEGORY_RANK[category] ?? UNRANKED;

export const compareAssignedMetrics = (left, right) =>
  rankCategory(left.category) - rankCategory(right.category) ||
  compareText(left.metricTitle, right.metricTitle) ||
  compareText(left.metricCode, right.metricCode);

export const formatCount = (value) =>
  new Intl.NumberFormat().format(Number(value) || 0);

/**
 * @param {AssignedMetric[]} metrics
 * @returns {AssignedMetric[]}
 */
export const normalizeAssignedMetrics = (metrics) => {
  const seen = new Set();
  const normalized = [];

  for (const metric of Array.isArray(metrics) ? metrics : []) {
    const key = String(metric?.metricId || metric?.metricCode || "");
    if (!key || seen.has(key)) continue;
    seen.add(key);

    normalized.push({
      metricId: key,
      metricCode: metric?.metricCode || "",
      metricTitle: metric?.metricTitle || "",
      framework: metric?.framework || "",
      category: metric?.category || "General",
      isCompleted: Boolean(metric?.isCompleted),
    });
  }

  return normalized.sort(compareAssignedMetrics);
};

/**
 * @param {AssignedMetric[]} metrics
 */
export const buildCategorySummary = (metrics) =>
  CATEGORY_ORDER.map((category) => {
    const categoryMetrics = metrics.filter(
      (metric) => metric.category === category,
    );
    const completed = categoryMetrics.filter(
      (metric) => metric.isCompleted,
    ).length;

    return {
      category,
      completed,
      total: categoryMetrics.length,
    };
  });

/**
 * @param {AssignedMetric[]} metrics
 * @param {string} search
 */
export const filterAssignedMetrics = (metrics, search) => {
  const query = search.trim().toLowerCase();
  if (!query) return metrics;

  return metrics.filter((metric) =>
    [metric.metricCode, metric.metricTitle]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query),
  );
};
